import { useEffect, useMemo, useState, useRef } from "react";
import { useTheme } from "../../../common/ThemeContext";
import { fetchAuth } from "../../../common/api";
import { fetchAlerts } from "../../../common/alertsApi.js";
import { useSeason } from "../../../common/SeasonContext";
import { usePlayersPerf } from "../../../common/PlayersPerfContext";
import { Users } from "lucide-react";
import ClubTrendsPanel from "./ClubTrendsPanel";
import { PdfExportProvider, usePdfExport } from "../../pdf/PdfExportContext";
import ExportClubReportMenu from "../../pdf/ExportClubReportMenu";
import { filterEnabledPlayers } from "../../../common/playerFilter";
import PlayerRiskModeToggle from "../../../common/PlayerRiskModeToggle.jsx";
import {
  buildActiveAlertPlayerIdSet,
  DEFAULT_TEAM_OVERVIEW_RISK_MODE,
  hasLoadSimulationData,
  isPlayerAtRisk,
  PLAYER_AT_RISK_TRAINABILITY_THRESHOLD,
  readTeamOverviewRiskMode,
  TEAM_OVERVIEW_RISK_MODE_AT_RISK,
  TEAM_OVERVIEW_RISK_MODE_STORAGE_KEY,
} from "../../../common/playerRiskUtils.js";
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ResponsiveContainer, Legend, Tooltip
} from "recharts";
import ChartCapture from "../../pdf/ChartCapture";

const TRAINABILITY_MAX_SCORE = 10;
const ALERTS_REFRESH_INTERVAL_MS = 60 * 1000;

/* small utils */
const fmtPct = (v) => (Number.isFinite(v) ? `${(v * 100).toFixed(1)}%` : "—");
const fmt = (v, suffix = "") => (Number.isFinite(v) ? `${v.toFixed(2)}${suffix}` : "—");
const fmtScore10 = (v) => (Number.isFinite(v) ? `${v.toFixed(1)}/10` : "—");
const scoreWidth = (v) => `${Math.max(0, Math.min(100, (v / TRAINABILITY_MAX_SCORE) * 100))}%`;
const avg = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : NaN);
function mean(arr){ return arr.length? arr.reduce((a,b)=>a+b,0)/arr.length : NaN; }
function std(arr){
  if(arr.length < 2) return 0;
  const m = mean(arr); const v = mean(arr.map(x => (x-m)**2));
  return Math.sqrt(v);
}
function clamp(v, lo, hi){ return Math.max(lo, Math.min(hi, v)); }

function normalizeTrainabilityScore(value, alreadyOutOf10) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return NaN;
  return clamp(alreadyOutOf10 ? numeric : numeric * TRAINABILITY_MAX_SCORE, 0, TRAINABILITY_MAX_SCORE);
}

function groupBy(arr, key) {
  return arr.reduce((acc, x) => {
    const k = x[key] || "N/A";
    (acc[k] ||= []).push(x);
    return acc;
  }, {});
}

// MOCK --- RNG déterministe par nom (stable, pas de flicker)
const hashStr = (s) => {
  let h = 1779033703 ^ s.length;
  for (let i = 0; i < s.length; i++) {
    h = Math.imul(h ^ s.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return (h >>> 0);
};
const mulberry32 = (seed) => {
  return function() {
    let t = (seed += 0x6D2B79F5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};
const randn = (rng) => {
  // Box–Muller
  let u = 0, v = 0;
  while (u === 0) u = rng();
  while (v === 0) v = rng();
  return Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
};
const sampleTruncNorm = (rng, mu, sigma, min, max) => {
  if (!Number.isFinite(sigma) || sigma <= 0) sigma = (max - min) * 0.08;
  for (let i = 0; i < 16; i++) {
    const val = mu + randn(rng) * sigma;
    if (val >= min && val <= max) return val;
  }
  return clamp(mu, min, max); // fallback
};

function normalizeExtrema(ext) {
   const out = {};
   for (const [k, v] of Object.entries(ext || {})) {
     out[k.toLowerCase()] = {
       min: Number.isFinite(v?.min) ? Math.max(0, Math.min(1, +v.min)) : 0,
       max: Number.isFinite(v?.max) ? Math.max(0, Math.min(1, +v.max)) : 1,
     };
   }
   return out;
 }

const USE_FRONT_MOCKS = false;
export default function ClubReport() {
  const { isDarkMode } = useTheme();
  const { season } = useSeason();
  const { players: contextPlayers, mostRecentSimulation } = usePlayersPerf();

  const [loading, setLoading] = useState(true);
  const [basePlayers, setBasePlayers] = useState([]);
  const [club, setClub] = useState(null);
  const [logo, setLogo] = useState(null);
  const [lastDate, setLastDate] = useState(null);
  const selectedPosition = "All";
  const [riskMode, setRiskMode] = useState(() => {
    if (typeof window === "undefined") return DEFAULT_TEAM_OVERVIEW_RISK_MODE;
    return readTeamOverviewRiskMode(window.sessionStorage);
  });
  const [activeAlertPlayerIds, setActiveAlertPlayerIds] = useState(() => new Set());

  const [caps, setCaps] = useState({
    sprint_max: 39,
    acceleration_max: 8,
    deceleration_max: 8,
  });
  const [zFactor, setZFactor] = useState(2);
  const augmentedOnce = useRef(false);
  const [serverWeekExtrema, setServerWeekExtrema] = useState(null);

  useEffect(() => {
    try {
      window.sessionStorage.setItem(TEAM_OVERVIEW_RISK_MODE_STORAGE_KEY, riskMode);
    } catch {
      // Session storage can be unavailable in embedded browser contexts.
    }
  }, [riskMode]);

  useEffect(() => {
    let cancelled = false;

    const refreshAlerts = async () => {
      try {
        const { alerts } = await fetchAlerts({ status: "all", limit: 200, offset: 0 });
        if (!cancelled) {
          setActiveAlertPlayerIds(buildActiveAlertPlayerIdSet(alerts));
        }
      } catch {
        if (!cancelled) {
          setActiveAlertPlayerIds(new Set());
        }
      }
    };

    refreshAlerts();
    const interval = setInterval(refreshAlerts, ALERTS_REFRESH_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  const players = useMemo(() => {
    if (riskMode !== TEAM_OVERVIEW_RISK_MODE_AT_RISK) return basePlayers;

    return basePlayers.filter((player) =>
      isPlayerAtRisk(player, {
        activeAlertPlayerIds,
        trainabilityThreshold: PLAYER_AT_RISK_TRAINABILITY_THRESHOLD,
      })
    );
  }, [basePlayers, riskMode, activeAlertPlayerIds]);

  useEffect(() => {
    (async () => {
      try {
        // identity
        const [cNameRes, cLogoRes, perfRes] = await Promise.all([
          fetchAuth(`/club-name`),
          fetchAuth(`/clubs/logo`),
          fetchAuth(`/playersperfs?season=${season}`),
        ]);
        const clubName = cNameRes.ok ? await cNameRes.json() : null;
        const clubLogo = cLogoRes.ok ? await cLogoRes.json() : null;
        const perfs    = perfRes.ok ? await perfRes.json() : { players: [] };

        // Use context players (enabled only) instead of API response
        const playersToUse = Array.isArray(contextPlayers) && contextPlayers.length > 0 
          ? contextPlayers 
          : (perfs.players || []);
        
        // Ensure players are filtered to enabled players with real load simulation data.
        const filteredPlayers = filterEnabledPlayers(playersToUse).filter(hasLoadSimulationData);
        
        setClub(clubName || null);
        setLogo(clubLogo?.logo || null);
        setBasePlayers(filteredPlayers);
        setLastDate(mostRecentSimulation || perfs.most_recent_simulation || null);
        setServerWeekExtrema(normalizeExtrema(perfs.club_week_extrema) || null);
      } catch (e) {
       
      } finally {
        setLoading(false);
      }
    })();
  }, [season, contextPlayers, mostRecentSimulation]);

  

  // --- MOCK: normaliser nom ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////:
  const normName = (f, l) =>
    `${(f || "").trim().toLowerCase()} ${(l || "").trim().toLowerCase()}`.trim();

  // --- MOCK: fabrique une perf cohérente pour un joueur manquant
  const buildMockPerf = (playerMeta, context) => {
    const {
      teamMeans, // tes moyennes d’équipe by key
      teamStd,   // std empiriques qu’on calcule ci-dessous
      caps,
      absDom,    // absoluteDomains
    } = context;

    const { first_name, last_name, position } = playerMeta;
    const seed = hashStr(`${first_name}|${last_name}|${position}|${season}`);
    const rng  = mulberry32(seed);

    // helpers bornes par métrique
    const lim = (k) => absDom[k] || { min: 0, max: 1 };

    // indices “index”
    const intensityPT = sampleTruncNorm(rng,
      (teamMeans.intensityPT ?? 1.2),
      (teamStd.intensityPT ?? 0.35),
      lim("intensityPT").min, lim("intensityPT").max
    );
    const mechanicalPT = sampleTruncNorm(rng,
      (teamMeans.mechanicalPT ?? 1.4),
      (teamStd.mechanicalPT ?? 0.40),
      lim("mechanicalPT").min, lim("mechanicalPT").max
    );

    // pourcentages [0,1]
    const rii = clamp(sampleTruncNorm(rng,
      (teamMeans.rii ?? 0.75),
      (teamStd.rii ?? 0.12),
      0.35, 0.98
    ), 0, 1);

    const mli = clamp(sampleTruncNorm(rng,
      (teamMeans.mli ?? 0.78),
      (teamStd.mli ?? 0.10),
      0.40, 1
    ), 0, 1);

    const fatigue = clamp(sampleTruncNorm(rng,
      (teamMeans.fatigue ?? 0.30),
      (teamStd.fatigue ?? 0.08),
      0.10, 0.65
    ), 0, 1);

    // ext (caps)
    const sprint = sampleTruncNorm(rng,
      (teamMeans.sprint ?? 29),
      (teamStd.sprint ?? 1.6),
      20, caps.sprint_max
    );
    const acc = sampleTruncNorm(rng,
      (teamMeans.acc ?? 4.6),
      (teamStd.acc ?? 0.5),
      2, caps.acceleration_max
    );
    const dec = sampleTruncNorm(rng,
      (teamMeans.dec ?? 4.6),
      (teamStd.dec ?? 0.5),
      2, caps.deceleration_max
    );

    // distance & COD: dérive depuis distributions observées si dispo
    const distance = Math.max(2, sampleTruncNorm(rng,
      (context.distMean ?? 8.5),
      (context.distStd ?? 2.0),
      1, 16
    ));
    const cod = Math.max(10, Math.round(sampleTruncNorm(rng,
      (context.codMean ?? 90),
      (context.codStd ?? 25),
      10, 220
    )));

    // “prev” comme légère variation
    const vary = (v, frac = 0.15, min = 0, max = Infinity) =>
      clamp(v * (1 + (rng() - 0.5) * 2 * frac), min, max);

    const intensityPT_prev  = vary(intensityPT,  0.25, lim("intensityPT").min, lim("intensityPT").max);
    const mechanicalPT_prev = vary(mechanicalPT, 0.25, lim("mechanicalPT").min, lim("mechanicalPT").max);
    const rii_prev          = clamp(vary(rii,     0.20, 0, 1), 0, 1);
    const mli_prev          = clamp(vary(mli,     0.20, 0, 1), 0, 1);
    const fatigue_prev      = clamp(vary(fatigue, 0.25, 0, 1), 0, 1);
    const sprint_prev       = vary(sprint, 0.10, 20, caps.sprint_max);
    const acc_prev          = vary(acc,    0.12,  2, caps.acceleration_max);
    const dec_prev          = vary(dec,    0.12,  2, caps.deceleration_max);
    const distance_prev     = vary(distance, 0.22, 1, 16);
    const cod_prev          = Math.max(5, Math.round(vary(cod, 0.30, 5, 250)));

    // construit l’objet perf conforme à /playersperfs
    return {
      id: `mock-${seed}`,
      first_name, last_name, position,
      isActive: true,
      profile_picture: null,
      timeinfo_date: "N/A",
      fatigue_index: 0,
      // indices LB
      intensity_index: intensityPT,
      intensity_index_prev: intensityPT_prev,
      mechanical_load_index: mechanicalPT,
      mechanical_load_index_prev: mechanicalPT_prev,
      mechanical_fatigue: fatigue,
      mechanical_fatigue_prev: fatigue_prev,
      // vitesse/charges ext
      sprint_max: sprint,
      sprint_max_prev: sprint_prev,
      acceleration_max: acc,
      acceleration_max_prev: acc_prev,
      deceleration_max: dec,
      deceleration_max_prev: dec_prev,
      global_distance: distance,
      global_distance_prev: distance_prev,
      COD_number: cod,
      COD_number_prev: cod_prev,
      // % indices relatifs
      MLI: mli, MLI_prev: mli_prev,
      RII: rii, RII_prev: rii_prev,
    };
  };

  // --- MOCK: calcule std/means rapides depuis joueurs actuels
  const teamStdByKey = useMemo(() => {
    const arrs = {
      intensityPT: players.map(p=>p.intensity_index).filter(Number.isFinite),
      mechanicalPT: players.map(p=>p.mechanical_load_index).filter(Number.isFinite),
      rii: players.map(p=>p.RII).filter(Number.isFinite),
      mli: players.map(p=>p.MLI).filter(Number.isFinite),
      fatigue: players.map(p=>p.mechanical_fatigue).filter(Number.isFinite),
      sprint: players.map(p=>p.sprint_max).filter(Number.isFinite),
      acc: players.map(p=>p.acceleration_max).filter(Number.isFinite),
      dec: players.map(p=>p.deceleration_max).filter(Number.isFinite),
    };
    const out = {};
    for (const k of Object.keys(arrs)) out[k] = std(arrs[k] || []);
    return out;
  }, [players]);

  ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  /* ---- Aggregations (team level) --------------------------------------- */
  const teamStats = useMemo(() => {
    if (!players.length) return null;

    // current
    const ii   = players.map(p => p.intensity_index).filter(Number.isFinite);
    const mech = players.map(p => p.mechanical_load_index).filter(Number.isFinite);
    const rii  = players.map(p => p.RII).filter(Number.isFinite);
    const mli  = players.map(p => p.MLI).filter(Number.isFinite);
    const fat  = players.map(p => p.mechanical_fatigue).filter(Number.isFinite);

    const accMax = players.map(p => p.acceleration_max).filter(Number.isFinite);
    const decMax = players.map(p => p.deceleration_max).filter(Number.isFinite);
    const sprint = players.map(p => p.sprint_max).filter(Number.isFinite);

    const distKm = players.map(p => p.global_distance).filter(Number.isFinite);
    const codNum = players.map(p => p.COD_number).filter(Number.isFinite);

    // previous
    const iiPrev   = players.map(p => p.intensity_index_prev).filter(Number.isFinite);
    const mechPrev = players.map(p => p.mechanical_load_index_prev).filter(Number.isFinite);
    const riiPrev  = players.map(p => p.RII_prev).filter(Number.isFinite);
    const mliPrev  = players.map(p => p.MLI_prev).filter(Number.isFinite);
    const fatPrev  = players.map(p => p.mechanical_fatigue_prev).filter(Number.isFinite);

    const accPrev = players.map(p => p.acceleration_max_prev).filter(Number.isFinite);
    const decPrev = players.map(p => p.deceleration_max_prev).filter(Number.isFinite);
    const sprPrev = players.map(p => p.sprint_max_prev).filter(Number.isFinite);

    const distPrev = players.map(p => p.global_distance_prev).filter(Number.isFinite);
    const codPrev  = players.map(p => p.COD_number_prev).filter(Number.isFinite);

    const cur = {
      avgIntensityPT: avg(ii),
      avgMechPT:      avg(mech),
      avgRII:         avg(rii),
      avgMLI:         avg(mli),
      avgFatigue:     avg(fat),
      avgAcc:         avg(accMax),
      avgDec:         avg(decMax),
      avgSprint:      avg(sprint),
      sumDistanceKm:  distKm.reduce((a,b)=>a+b,0),
      sumCod:         codNum.reduce((a,b)=>a+b,0),
    };
    const prev = {
      avgIntensityPT: avg(iiPrev),
      avgMechPT:      avg(mechPrev),
      avgRII:         avg(riiPrev),
      avgMLI:         avg(mliPrev),
      avgFatigue:     avg(fatPrev),
      avgAcc:         avg(accPrev),
      avgDec:         avg(decPrev),
      avgSprint:      avg(sprPrev),
      sumDistanceKm:  distPrev.reduce((a,b)=>a+b,0),
      sumCod:         codPrev.reduce((a,b)=>a+b,0),
    };

    return {
      countPlayers: players.length,
      ...cur,
      deltas: {
        avgIntensityPT: cur.avgIntensityPT - prev.avgIntensityPT,
        avgMechPT:      cur.avgMechPT      - prev.avgMechPT,
        avgRII:         cur.avgRII         - prev.avgRII,
        avgMLI:         cur.avgMLI         - prev.avgMLI,
        avgFatigue:     cur.avgFatigue     - prev.avgFatigue,
        avgAcc:         cur.avgAcc         - prev.avgAcc,
        avgDec:         cur.avgDec         - prev.avgDec,
        avgSprint:      cur.avgSprint      - prev.avgSprint,
        sumDistanceKm:  cur.sumDistanceKm  - prev.sumDistanceKm,
        sumCod:         cur.sumCod         - prev.sumCod,
      }
    };
  }, [players]);

  /* ---- Per-position breakdown ----------------------------------------- */
  const byPosition = useMemo(() => {
    const g = groupBy(players, "position");
    const rows = Object.entries(g).map(([pos, arr]) => {
      const cur = {
        intensityPT: avg(arr.map(p => p.intensity_index).filter(Number.isFinite)),
        mechanicalPT: avg(arr.map(p => p.mechanical_load_index).filter(Number.isFinite)),
        rii: avg(arr.map(p => p.RII).filter(Number.isFinite)),
        mli: avg(arr.map(p => p.MLI).filter(Number.isFinite)),
        fatigue: avg(arr.map(p => p.mechanical_fatigue).filter(Number.isFinite)),
        acc: avg(arr.map(p => p.acceleration_max).filter(Number.isFinite)),
        dec: avg(arr.map(p => p.deceleration_max).filter(Number.isFinite)),
        sprint: avg(arr.map(p => p.sprint_max).filter(Number.isFinite)),
      };
      const prev = {
        intensityPT: avg(arr.map(p => p.intensity_index_prev).filter(Number.isFinite)),
        mechanicalPT: avg(arr.map(p => p.mechanical_load_index_prev).filter(Number.isFinite)),
        rii: avg(arr.map(p => p.RII_prev).filter(Number.isFinite)),
        mli: avg(arr.map(p => p.MLI_prev).filter(Number.isFinite)),
        fatigue: avg(arr.map(p => p.mechanical_fatigue_prev).filter(Number.isFinite)),
        acc: avg(arr.map(p => p.acceleration_max_prev).filter(Number.isFinite)),
        dec: avg(arr.map(p => p.deceleration_max_prev).filter(Number.isFinite)),
        sprint: avg(arr.map(p => p.sprint_max_prev).filter(Number.isFinite)),
      };
      return {
        pos, n: arr.length, ...cur,
        d_intensityPT: cur.intensityPT - prev.intensityPT,
        d_mechanicalPT: cur.mechanicalPT - prev.mechanicalPT,
        d_rii: cur.rii - prev.rii,
        d_mli: cur.mli - prev.mli,
        d_fatigue: cur.fatigue - prev.fatigue,
        d_acc: cur.acc - prev.acc,
        d_dec: cur.dec - prev.dec,
        d_sprint: cur.sprint - prev.sprint,
      };
    });
    const order = ["Defense","Midfield","Forward","Lateral","N/A"];
    rows.sort((a,b)=> order.indexOf(a.pos) - order.indexOf(b.pos));
    return rows;
  }, [players]);

  const baseTrainabilityRows = useMemo(() => {
    const trainabilityValues = players
      .flatMap(p => [p.trainability_index, p.trainability_index_prev])
      .map(Number)
      .filter(Number.isFinite);
    const isAlreadyOutOf10 = trainabilityValues.some(v => Math.abs(v) > 1);

    return players
      .map(p => {
        const train = normalizeTrainabilityScore(p.trainability_index, isAlreadyOutOf10);
        const trainPrev = normalizeTrainabilityScore(p.trainability_index_prev, isAlreadyOutOf10);
        const delta =
          Number.isFinite(train) && Number.isFinite(trainPrev)
            ? train - trainPrev
            : NaN;

        return {
          id: p.id,
          name: `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim(),
          position: p.position || "N/A",
          score: train,
          scorePrev: trainPrev,
          // on garde `rii` pour rester compatible avec l’export PDF existant
          rii: train,
          riiPrev: trainPrev,
          delta,
          avatar: p.profile_picture
            ? `data:image/png;base64,${p.profile_picture}`
            : null,
        };
      })
      .filter(r => Number.isFinite(r.score));
  }, [players]);

  const lowestTrainabilityPlayers = useMemo(() => {
    return [...baseTrainabilityRows]
      .sort((a, b) => a.score - b.score)
      .slice(0, 3);
  }, [baseTrainabilityRows]);

  const bestTrainabilityPlayers = useMemo(() => {
    return [...baseTrainabilityRows]
      .sort((a, b) => b.score - a.score)
      .slice(0, 3);
  }, [baseTrainabilityRows]);

  const pageCls = `${isDarkMode ? "bg-gray-900 text-white" : "bg-gray-100 text-black"} min-h-screen`;
  const cardCls = `rounded-2xl p-5 border ${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`;

  // liste des métriques du radar
  const METRICS = [
    { key: "intensityPT", label: "Intensity (PT)", kind: "index" },
    { key: "mechanicalPT", label: "Mechanical (PT)", kind: "index" },
    { key: "rii",         label: "RII",             kind: "index" },
    { key: "mli",         label: "MLI",             kind: "index" },
    { key: "fatigue",     label: "Fatigue",         kind: "index" },
    { key: "acc",         label: "Acc (m/s²)",      kind: "ext"   },
    { key: "dec",         label: "Dec (m/s²)",      kind: "ext"   },
    { key: "sprint",      label: "Sprint (km/h)",   kind: "ext"   },
  ];

  const PCT_KEYS = ["rii", "mli", "fatigue"];

  // palette améliorée (clair/sombre)
  const RADAR_COLORS = (isDark) => ({
    // bande min↔max
    bandMaxFill:  isDark ? "rgba(125,211,252,0.22)" : "rgba(59,130,246,0.18)", // bleu clair
    bandMinFill:  isDark ? "#1F2937" : "#FFFFFF", // ← couleur du fond de carte (paper) pour "creuser"
    bandStroke:   isDark ? "#60A5FA" : "#2563EB",

    // moyennes
    posStroke:    isDark ? "#A78BFA" : "#7C3AED", // violet (poste)
    teamStroke:   isDark ? "#34D399" : "#059669", // émeraude (équipe)
    grid:         isDark ? "#374151" : "#E5E7EB",
  });

  const SHORT_LABELS = {
    "Intensity (PT)": "Intensity",
    "Mechanical (PT)": "Mechanical",
    "RII": "RII",
    "MLI": "MLI",
    "Fatigue": "Fatigue",
    "Acc (m/s²)": "Acc",
    "Dec (m/s²)": "Dec",
    "Sprint (km/h)": "Sprint",
  };


  const LegendChips = ({ colors }) => (
    <div className="flex items-center justify-center gap-3 text-xs select-none">
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-gray-300 dark:border-gray-600">
        <span className="inline-block w-3 h-1.5 rounded-sm" style={{ background: colors.bandMaxFill, border: `1px solid ${colors.bandStroke}` }} />
        Plage (min→max)
      </span>
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-gray-300 dark:border-gray-600">
        <span className="inline-block w-3 h-1 rounded-sm" style={{ background: colors.posStroke }} />
        Moy. poste
      </span>
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-gray-300 dark:border-gray-600">
        <span className="inline-block w-3 h-1 rounded-sm" style={{ background: colors.teamStroke }} />
        Moy. équipe
      </span>
    </div>
  );

  // Passe-lui { colors: C, isDark: isDarkMode }
  const RadarTooltip = ({ active, payload, colors, isDark }) => {
    if (!active || !payload || !payload.length) return null;
    const p = payload[0]?.payload || {};
    const fmtVal = (x, isPct) =>
      Number.isFinite(x) ? (isPct ? `${(x * 100).toFixed(1)}%` : x.toFixed(2)) : "—";

    const paperBg   = isDark ? "bg-gray-800/95" : "bg-white/95";
    const borderClr = isDark ? "border-gray-700" : "border-gray-200";
    const textMain  = isDark ? "text-gray-100" : "text-gray-900";
    const textMute  = isDark ? "text-gray-300" : "text-gray-600";

    return (
      <div
        className={[
          "rounded-lg", "border", borderClr, paperBg, "backdrop-blur",
          "px-3", "py-2.5", "text-[11.5px]", "leading-tight", "shadow-lg", "min-w-[200px]"
        ].join(" ")}
      >
        {/* Header avec filet coloré */}
        <div className="flex items-center gap-2 mb-2">
          <span
            className="inline-block w-1.5 h-4 rounded-sm"
            style={{ background: colors.bandStroke }}
            aria-hidden
          />
          <div className={`font-medium ${textMain}`}>{p.metric}</div>
        </div>

        {/* Légende compacte */}
        <div className={`flex items-center gap-2 mb-2 ${textMute}`}>
          <span className="inline-flex items-center gap-1">
            <span
              className="inline-block w-3 h-2 rounded-sm ring-1"
              style={{ background: colors.bandMaxFill, boxShadow: `inset 0 0 0 1px ${colors.bandStroke}` }}
            />
            Range
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-3 h-0.5 rounded-sm" style={{ background: colors.posStroke }} />
            Position
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-3 h-0.5 rounded-sm" style={{ background: colors.teamStroke }} />
            Team
          </span>
        </div>

        {/* Valeurs alignées */}
        <div className="grid grid-cols-2 gap-x-3 gap-y-1">
          <div className={textMute}>Min</div>
          <div className={`text-right tabular-nums ${textMain}`}>{fmtVal(p.rawMin, p.isPct)}</div>

          <div className={textMute}>Max</div>
          <div className={`text-right tabular-nums ${textMain}`}>{fmtVal(p.rawMax, p.isPct)}</div>

          <div className={textMute}>Position</div>
          <div className={`text-right tabular-nums ${textMain}`}>
            <span
              className="inline-block w-2 h-0.5 rounded-sm mr-1 align-middle"
              style={{ background: colors.posStroke }}
              aria-hidden
            />
            {fmtVal(p.rawPos, p.isPct)}
          </div>

          <div className={textMute}>Team</div>
          <div className={`text-right tabular-nums ${textMain}`}>
            <span
              className="inline-block w-2 h-0.5 rounded-sm mr-1 align-middle"
              style={{ background: colors.teamStroke }}
              aria-hidden
            />
            {fmtVal(p.rawTeam, p.isPct)}
          </div>
        </div>
      </div>
    );
  };

  // valeurs brutes par joueur pour chaque clé (pour stats équipe)
  const teamRawByKey = useMemo(() => {
    return {
      intensityPT: players.map(p => p.intensity_index).filter(Number.isFinite),
      mechanicalPT: players.map(p => p.mechanical_load_index).filter(Number.isFinite),
      rii: players.map(p => p.RII).filter(Number.isFinite),
      mli: players.map(p => p.MLI).filter(Number.isFinite),
      fatigue: players.map(p => p.mechanical_fatigue).filter(Number.isFinite),
      acc: players.map(p => p.acceleration_max).filter(Number.isFinite),
      dec: players.map(p => p.deceleration_max).filter(Number.isFinite),
      sprint: players.map(p => p.sprint_max).filter(Number.isFinite),
    };
  }, [players]);

  const clubWeekExtrema = useMemo(() => {
    if (serverWeekExtrema) return serverWeekExtrema;
    // fallback local si backend pas encore déployé
    const ext = {};
    for (const key of PCT_KEYS) {
      const arr = (teamRawByKey[key] || []).filter(Number.isFinite);
      let mn = arr.length ? Math.min(...arr) : 0;
      let mx = arr.length ? Math.max(...arr) : 1;
      mn = Math.max(0, mn);
      mx = Math.min(1, Math.max(mn + 1e-6, mx));
      ext[key] = { min: mn, max: mx };
    }
    return ext;
  }, [serverWeekExtrema, teamRawByKey]);



  // moyennes d'équipe pour l'affichage "moyenne équipe" sur le radar
  const teamMeans = useMemo(() => {
    const out = {};
    for (const m of METRICS) out[m.key] = mean(teamRawByKey[m.key] || []);
    return out;
  }, [teamRawByKey]);

  // domaines par métrique (min/max) avec z-factor & caps
  const metricDomains = useMemo(() => {
    const domains = {};
    for (const m of METRICS) {
      if (PCT_KEYS.includes(m.key)) {
        const { min, max } = clubWeekExtrema[m.key] || { min:0, max:1 };
        domains[m.key] = { min, max };
        continue; // on passe au metric suivant, sans z-factor
      }
      const arr = teamRawByKey[m.key] || [];
      if (!arr.length) { domains[m.key] = { min: 0, max: 1 }; continue; }

      let minV, maxV;

      if (m.kind === "index") {
        const mu  = mean(arr);
        const sig = std(arr);
        // si σ = 0, on fixe un petit intervalle autour de μ
        const span = sig > 0 ? zFactor * sig : Math.max(Math.abs(mu)*0.05, 0.05);
        minV = mu - span;
        maxV = mu + span;
        // indices attendus entre 0 et 1 pour RII/MLI/Fatigue, mais PT peuvent dépasser 1.
        // on ne clippe pas agressivement, on laisse le z faire foi :
        if (["rii","mli","fatigue"].includes(m.key)) {
          minV = Math.max(0, minV);
          maxV = Math.min(1, maxV);
          if (minV === maxV) { minV = 0; maxV = 1; }
        } else {
          // pour PT, on évite min==max
          if (minV === maxV) { minV -= 0.5; maxV += 0.5; }
        }
      } else {
        // metrics "ext": appliquer caps “métier”
        const rawMin = Math.min(...arr);
        const rawMax = Math.max(...arr);
        const hardMax =
          m.key === "sprint" ? caps.sprint_max :
          m.key === "acc"    ? caps.acceleration_max :
          m.key === "dec"    ? caps.deceleration_max : undefined;

        minV = rawMin;
        maxV = (hardMax ?? rawMax);
        // sécurité: si tous > hardMax, garder hardMax comme max et min au moins 0
        if (hardMax !== undefined) {
          maxV = Math.min(maxV, hardMax);
        }
        if (minV >= maxV) {
          // créer une petite ouverture si confus
          minV = Math.max(0, maxV - 0.5);
        }
      }

      domains[m.key] = { min: minV, max: maxV };
    }
    return domains;
  }, [teamRawByKey, caps, zFactor]);

  const radarByPosition = useMemo(() => {
    const g = groupBy(players, "position");
    // positions dans un ordre cohérent
    const order = ["Defense","Midfield","Forward","Lateral","N/A"];
    return Object.entries(g)
      .map(([pos, arr]) => {
        const cur = {
          intensityPT: mean(arr.map(p => p.intensity_index).filter(Number.isFinite)),
          mechanicalPT: mean(arr.map(p => p.mechanical_load_index).filter(Number.isFinite)),
          rii: mean(arr.map(p => p.RII).filter(Number.isFinite)),
          mli: mean(arr.map(p => p.MLI).filter(Number.isFinite)),
          fatigue: mean(arr.map(p => p.mechanical_fatigue).filter(Number.isFinite)),
          acc: mean(arr.map(p => p.acceleration_max).filter(Number.isFinite)),
          dec: mean(arr.map(p => p.deceleration_max).filter(Number.isFinite)),
          sprint: mean(arr.map(p => p.sprint_max).filter(Number.isFinite)),
          n: arr.length,
          pos,
        };
        return cur;
      })
      .sort((a,b)=> order.indexOf(a.pos) - order.indexOf(b.pos));
  }, [players]);

  const norm01 = (x, lo, hi) => {
    if (!Number.isFinite(x) || !Number.isFinite(lo) || !Number.isFinite(hi) || hi <= lo) return 0;
    return clamp((x - lo) / (hi - lo), 0, 1);
  };

  const absoluteDomains = useMemo(() => {
    const abs = {};
    for (const m of METRICS) {
      if (PCT_KEYS.includes(m.key)) {
        abs[m.key] = { min: 0, max: 1 };
        continue;
      }
      if (m.kind === "ext") {
        const hardMax =
          m.key === "sprint" ? caps.sprint_max :
          m.key === "acc"    ? caps.acceleration_max :
          m.key === "dec"    ? caps.deceleration_max : undefined;
        // centre 0, max = cap (ou fallback)
        abs[m.key] = { min: 0, max: hardMax ?? Math.max(1, (metricDomains[m.key]?.max ?? 1)) };
        continue;
      }
      // index PT (non borné à 1) : centre 0, plafond dérivé du z-domain + un peu de marge
      const zMax = metricDomains[m.key]?.max ?? 1;
      const teamMean = teamMeans[m.key] ?? 0;
      const ceil = Math.max(1, zMax, teamMean) * 1.05; // padding 5% pour éviter d'écraser en haut
      abs[m.key] = { min: 0, max: ceil };
    }
    return abs;
  }, [METRICS, PCT_KEYS, caps, metricDomains, teamMeans]);

  // NEW: structure prête pour le RadarChart (bande min↔max sur un domaine ABSOLU centré à 0)
  const radarPrepared = useMemo(() => {
    const norm = (x, lo, hi) => (!Number.isFinite(x) || hi <= lo) ? 0 : clamp((x - lo) / (hi - lo), 0, 1);

    return radarByPosition.map(row => {
      const points = METRICS.map(m => {
        const domRel = (PCT_KEYS.includes(m.key) ? clubWeekExtrema[m.key] : metricDomains[m.key]) || { min:0, max:1 }; // min/max affichés (seuils)
        const domAbs = PCT_KEYS.includes(m.key)
           ? { min: 0, max: 1 }
           : (absoluteDomains[m.key] || { min:0, max:1 });

        const rawPos  = row[m.key];
        const rawTeam = teamMeans[m.key];

        // IMPORTANT: on normalise tout sur le domaine ABSOLU (0 → abs.max)
        const minBand = norm(domRel.min, domAbs.min, domAbs.max);
        const maxBand = norm(domRel.max, domAbs.min, domAbs.max);
        const posMean = norm(rawPos,     domAbs.min, domAbs.max);
        const teamMean= norm(rawTeam,    domAbs.min, domAbs.max);

        return {
          metric: m.label,
          key: m.key,
          // bandes
          min:  minBand,  // rempli couleur "paper" pour creuser
          max:  maxBand,  // rempli semi-transparent pour matérialiser la couronne [min,max]
          // lignes moyennes
          pos:  posMean,
          team: teamMean,
          // infos brutes tooltip
          rawMin: domRel.min,
          rawMax: domRel.max,
          rawPos,
          rawTeam,
          isPct: PCT_KEYS.includes(m.key),
          absMin: domAbs.min,
          absMax: domAbs.max,
        };
      });
      return { pos: row.pos, n: row.n, points };
    });
  }, [radarByPosition, METRICS, PCT_KEYS, clubWeekExtrema, metricDomains, absoluteDomains, teamMeans]);

  

  // --- MOCK: compléter l’effectif si USE_FRONT_MOCKS
  useEffect(() => {
    if (!USE_FRONT_MOCKS) return;
    if (augmentedOnce.current) return;
    if (!lastDate) return;

    (async () => {
      try {
        // 1) récupérer l’historique hebdo pour obtenir l’effectif (noms + positions)
        const res = await fetchAuth(`/players_weekly_history?date=${encodeURIComponent(lastDate)}&period=week&season=${encodeURIComponent(season)}`);
        if (!res.ok) throw new Error(`players_weekly_history ${res.status}`);
        const weekly = await res.json();
        const roster = Array.isArray(weekly) ? weekly : [];

        // si pas d’effectif exploitable, on stoppe
        if (!roster.length) return;

        // 2) index des perfs existantes par nom
        const have = new Map(
          (basePlayers || []).map(p => [normName(p.first_name, p.last_name), p])
        );

        // 3) stats globales utiles pour mock
        const distArr = basePlayers.map(p=>p.global_distance).filter(Number.isFinite);
        const codArr  = basePlayers.map(p=>p.COD_number).filter(Number.isFinite);

        const context = {
          teamMeans: {
            intensityPT: mean(basePlayers.map(p=>p.intensity_index).filter(Number.isFinite)),
            mechanicalPT: mean(basePlayers.map(p=>p.mechanical_load_index).filter(Number.isFinite)),
            rii: mean(basePlayers.map(p=>p.RII).filter(Number.isFinite)),
            mli: mean(basePlayers.map(p=>p.MLI).filter(Number.isFinite)),
            fatigue: mean(basePlayers.map(p=>p.mechanical_fatigue).filter(Number.isFinite)),
            sprint: mean(basePlayers.map(p=>p.sprint_max).filter(Number.isFinite)),
            acc: mean(basePlayers.map(p=>p.acceleration_max).filter(Number.isFinite)),
            dec: mean(basePlayers.map(p=>p.deceleration_max).filter(Number.isFinite)),
          },
          teamStd: teamStdByKey,
          caps,
          absDom: absoluteDomains,
          distMean: mean(distArr), distStd: std(distArr),
          codMean: mean(codArr),   codStd: std(codArr),
        };

        // 4) générer des perfs mockées pour les manquants
        const mocks = [];
        for (const r of roster) {
          const key = normName(r.first_name || r.firstName, r.last_name || r.lastName);
          if (!key) continue;
          if (have.has(key)) continue; // déjà présent dans /playersperfs
          const meta = {
            first_name: r.first_name || r.firstName || "",
            last_name:  r.last_name  || r.lastName  || "",
            position:   r.position   || "N/A",
          };
          mocks.push(buildMockPerf(meta, context));
        }

        if (mocks.length) {
          augmentedOnce.current = true;
          setBasePlayers(prev => [...prev, ...mocks]);
        }
      } catch (e) {
      
      }
    })();
  }, [USE_FRONT_MOCKS, lastDate, season, basePlayers, absoluteDomains, caps, teamStdByKey]);

  function PdfDataSync({ bestTrainability, lowestTrainability }) {
    const exporter = usePdfExport();
    useEffect(() => {
      exporter.setData("__topRii", Array.isArray(bestTrainability) ? bestTrainability : []);
      exporter.setData("__lowTrain", Array.isArray(lowestTrainability) ? lowestTrainability : []);
    }, [exporter, bestTrainability, lowestTrainability]);
    return null;
  }


  if (loading) return <div className={pageCls}><div className="p-6">Loading…</div></div>;

  return (
    <div className={pageCls}>
      <PdfExportProvider>
        <PdfDataSync
          bestTrainability={bestTrainabilityPlayers}
          lowestTrainability={lowestTrainabilityPlayers}
        />
        <div className="max-w-7xl mx-auto p-6 space-y-6">

          <div className={`${cardCls} flex flex-wrap items-center gap-4`}>
          {logo ? (
            <img src={logo} alt="club" className="w-14 h-14 rounded-lg object-contain bg-white dark:bg-gray-100" />
          ) : (
            <div className="w-14 h-14 rounded-lg flex items-center justify-center bg-gray-500/20">
            <Users />
            </div>
          )}
          <div className="flex-1">
            <h1 className="text-2xl font-bold">
            {club?.name ?? "Club"} — <span className="font-normal">{season}</span>
            </h1>
            <p className={`${isDarkMode ? "text-gray-300" : "text-gray-600"} text-sm`}>
            Team-level loads and performance indicators (latest simulation).
            </p>
          </div>
          {USE_FRONT_MOCKS && (
            <span className="px-2 py-1 text-xs font-medium text-white bg-rose-500 rounded">
            Mock Data
            </span>
          )}
          <PlayerRiskModeToggle
            isDarkMode={isDarkMode}
            riskMode={riskMode}
            onChange={setRiskMode}
          />
          <ExportClubReportMenu
            season={season}
            teamStats={teamStats}
            byPosition={byPosition}
            topRii={bestTrainabilityPlayers}
            lowTrain={lowestTrainabilityPlayers}
            dark={isDarkMode}
          />
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
            <div className="opacity-70">Players</div>
            <div className="text-xl font-semibold">{teamStats?.countPlayers ?? 0}</div>
            </div>
            <div>
            <div className="opacity-70">Total COD</div>
            <div className="text-xl font-semibold">{teamStats?.sumCod ?? 0}</div>
            </div>
          </div>
          </div>

          {/* ===== KPI grid (team averages) ===== */}
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-4">
            <Kpi title="Intensity (PT)"  value={fmt(teamStats?.avgIntensityPT)} delta={teamStats?.deltas?.avgIntensityPT} decimals={2} />
            <Kpi title="Mechanical (PT)" value={fmt(teamStats?.avgMechPT)}      delta={teamStats?.deltas?.avgMechPT}      decimals={2} />
            <Kpi title="RII"             value={fmtPct(teamStats?.avgRII)}      delta={teamStats?.deltas?.avgRII}         isPercent decimals={1} />
            <Kpi title="MLI"             value={fmtPct(teamStats?.avgMLI)}      delta={teamStats?.deltas?.avgMLI}         isPercent decimals={1} />
            <Kpi title="Fatigue"         value={fmtPct(teamStats?.avgFatigue)}  delta={teamStats?.deltas?.avgFatigue}     isPercent decimals={1} />
            <Kpi title="Acc max"         value={fmt(teamStats?.avgAcc,   " m/s²")} delta={teamStats?.deltas?.avgAcc}   unit=" m/s²" decimals={2} />
            <Kpi title="Dec max"         value={fmt(teamStats?.avgDec,   " m/s²")} delta={teamStats?.deltas?.avgDec}   unit=" m/s²" decimals={2} />
            <Kpi title="Sprint max"      value={fmt(teamStats?.avgSprint," km/h")} delta={teamStats?.deltas?.avgSprint} unit=" km/h" decimals={2} />
          </div>

          {/* ===== Trainability focus ===== */}
          <div className={`grid grid-cols-1 md:grid-cols-2 gap-4`}>
            <div className={cardCls}>
              <h3 className="font-semibold mb-2">Lowest performance scores</h3>

              {lowestTrainabilityPlayers.length === 0 ? (
                <p className={`${isDarkMode ? "text-gray-300" : "text-gray-700"} text-sm`}>
                  No trainability score available.
                </p>
              ) : (
                <ul className="space-y-2">
                  {lowestTrainabilityPlayers.map((p, i) => (
                    <li key={p.id} className="flex items-center gap-3">
                      {/* avatar */}
                      <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-200 flex items-center justify-center">
                        {p.avatar ? (
                          <img src={p.avatar} alt={p.name} className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-xs opacity-70">N/A</span>
                        )}
                      </div>

                      {/* infos */}
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">
                            {i + 1}. {p.name}
                          </span>
                          <span
                            className={`text-[11px] px-1.5 py-0.5 rounded border ${
                              isDarkMode
                                ? "border-gray-600 text-gray-300"
                                : "border-gray-300 text-gray-700"
                            }`}
                          >
                            {p.position}
                          </span>
                        </div>

                        {/* barre de progression trainability */}
                        <div className="mt-1">
                          <div
                            className={`h-1.5 rounded ${
                              isDarkMode ? "bg-gray-700" : "bg-gray-200"
                            }`}
                          >
                            <div
                              className="h-1.5 rounded bg-sky-500"
                              style={{
                                width: scoreWidth(p.score),
                              }}
                            />
                          </div>
                        </div>

                        {/* valeur & delta */}
                        <div className="mt-1 flex items-center justify-between">
                          <span
                            className={`${
                              isDarkMode ? "text-gray-200" : "text-gray-800"
                            } text-sm font-semibold`}
                          >
                            {fmtScore10(p.score)}
                          </span>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className={cardCls}>
              <h3 className="font-semibold mb-2">Best performance scores</h3>

              {bestTrainabilityPlayers.length === 0 ? (
                <p className={`${isDarkMode ? "text-gray-300" : "text-gray-700"} text-sm`}>
                  No trainability score available.
                </p>
              ) : (
                <ul className="space-y-2">
                  {bestTrainabilityPlayers.map((p, i) => (
                    <li key={p.id} className="flex items-center gap-3">
                      {/* avatar */}
                      <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-200 flex items-center justify-center">
                        {p.avatar ? (
                          <img src={p.avatar} alt={p.name} className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-xs opacity-70">N/A</span>
                        )}
                      </div>

                      {/* infos */}
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">
                            {i + 1}. {p.name}
                          </span>
                          <span
                            className={`text-[11px] px-1.5 py-0.5 rounded border ${
                              isDarkMode
                                ? "border-gray-600 text-gray-300"
                                : "border-gray-300 text-gray-700"
                            }`}
                          >
                            {p.position}
                          </span>
                        </div>

                        {/* barre trainability */}
                        <div className="mt-1">
                          <div
                            className={`h-1.5 rounded ${
                              isDarkMode ? "bg-gray-700" : "bg-gray-200"
                            }`}
                          >
                            <div
                              className="h-1.5 rounded bg-amber-500"
                              style={{
                                width: scoreWidth(p.score),
                              }}
                            />
                          </div>
                        </div>

                        {/* valeur & delta */}
                        <div className="mt-1 flex items-center justify-between">
                          <span
                            className={`${
                              isDarkMode ? "text-gray-200" : "text-gray-800"
                            } text-sm font-semibold`}
                          >
                            {fmtScore10(p.score)}
                          </span>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className={cardCls}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold">Per-position radar (team vs position)</h3>

              {/* Filtres caps + z */}
              <div className="flex flex-wrap items-center gap-3 text-xs">
                <div className="flex items-center gap-1">
                  <label className="opacity-70">Sprint max</label>
                  <input
                    type="number" step="0.1" min="1"
                    value={caps.sprint_max}
                    onChange={e => setCaps(c => ({...c, sprint_max: parseFloat(e.target.value)||39}))}
                    className={`w-20 px-2 py-1 border rounded ${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-300"}`}
                    title="Cap for Sprint (km/h)"
                  />
                </div>
                <div className="flex items-center gap-1">
                  <label className="opacity-70">Acc max</label>
                  <input
                    type="number" step="0.1" min="1"
                    value={caps.acceleration_max}
                    onChange={e => setCaps(c => ({...c, acceleration_max: parseFloat(e.target.value)||8}))}
                    className={`w-20 px-2 py-1 border rounded ${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-300"}`}
                    title="Cap for Acc (m/s²)"
                  />
                </div>
                <div className="flex items-center gap-1">
                  <label className="opacity-70">Dec max</label>
                  <input
                    type="number" step="0.1" min="1"
                    value={caps.deceleration_max}
                    onChange={e => setCaps(c => ({...c, deceleration_max: parseFloat(e.target.value)||8}))}
                    className={`w-20 px-2 py-1 border rounded ${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-300"}`}
                    title="Cap for Dec (m/s²)"
                  />
                </div>
                <div className="flex items-center gap-1">
                  <label className="opacity-70">z-score</label>
                  <input
                    type="number" step="0.1" min="0.5" max="4"
                    value={zFactor}
                    onChange={e => setZFactor(parseFloat(e.target.value)||2)}
                    className={`w-20 px-2 py-1 border rounded ${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-300"}`}
                    title="Z used for index min/max = μ ± zσ"
                  />
                </div>
              </div>
            </div>

            {/* Grille responsive de radars, un par poste */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
              {radarPrepared.map(({ pos, n, points }) => {
                const C = RADAR_COLORS(isDarkMode);
                return (
                  <div key={pos} className="rounded-xl border p-3 dark:border-gray-700 border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-medium">{pos}</div>
                      <div className={isDarkMode ? "text-gray-300" : "text-gray-600"}>n={n}</div>
                    </div>

                    <ChartCapture imageKey={`radar.position.${pos}`}>
                      <div style={{ width: "100%", height: 300 }}>
                        <ResponsiveContainer>
                          <RadarChart data={points} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                            <PolarGrid stroke={C.grid} />
                            <PolarAngleAxis
                              dataKey="metric"
                              tick={{ fontSize: 9 }}
                              tickLine={false} 
                              allowDuplicatedCategory={false}
                              tickFormatter={(v) => SHORT_LABELS[v] || v}
                            />
                            <PolarRadiusAxis angle={90} domain={[0, 1]} tick={false} />

                            {/* 1) Bande MAX : de 0 → max  (semi-transparent) */}
                            <Radar
                              name="Max range"
                              dataKey="max"
                              stroke={C.bandStroke}
                              fill={C.bandMaxFill}
                              fillOpacity={1}
                              strokeWidth={1}
                              isAnimationActive={false}
                            />

                            {/* 2) Bande MIN : de 0 → min  (remplie avec la couleur du papier pour creuser la zone centrale) */}
                            <Radar
                              name="Min range"
                              dataKey="min"
                              stroke="transparent"
                              fill={C.bandMinFill}
                              fillOpacity={1}
                              isAnimationActive={false}
                            />

                            {/* 3) Moyenne du poste (ligne) */}
                            <Radar
                              name="Avg. position"
                              dataKey="pos"
                              stroke={C.posStroke}
                              fill="transparent"
                              strokeWidth={2}
                              dot={false}
                              isAnimationActive={false}
                            />

                            {/* 4) Moyenne équipe (ligne) */}
                            <Radar
                              name="Avg. team"
                              dataKey="team"
                              stroke={C.teamStroke}
                              fill="transparent"
                              strokeWidth={2}
                              dot={false}
                              isAnimationActive={false}
                            />

                            <Tooltip
                              content={<RadarTooltip colors={C} isDark={isDarkMode} />}
                              cursor={false}
                              position={{ x: 10, y: 8 }}
                              wrapperStyle={{ pointerEvents: "none" }}
                              allowEscapeViewBox={{ x: true, y: true }}
                            />
                          </RadarChart>
                        </ResponsiveContainer>
                      </div>
                    </ChartCapture>
                  </div>
                );
              })}
            </div>
          </div>

          <ClubTrendsPanel
              selectedDate={lastDate}
              selectedPosition={selectedPosition}
              riskMode={riskMode}
              activeAlertPlayerIds={activeAlertPlayerIds}
              />

        </div>
        </PdfExportProvider>
    </div>
  );
}

function DeltaChip({ delta, isPercent=false, unit="", decimals, label="vs prev." }) {
  const eps = isPercent ? 0.001 : 0.005;
  const trend = !Number.isFinite(delta)
    ? "flat"
    : delta > eps ? "up" : delta < -eps ? "down" : "flat";

  const absVal = Math.abs(delta) * (isPercent ? 100 : 1);
  const dps = Number.isInteger(decimals) ? decimals : (isPercent ? 1 : 2);
  const sign = trend === "up" ? "+" : trend === "down" ? "−" : "=";
  const text = `${sign}${absVal.toFixed(dps)}${isPercent ? "%" : unit}`;

  // Fond blanc en light ET en dark, liseré + texte colorés
  const cls =
    trend === "up"
      ? "bg-white dark:bg-white text-emerald-600 ring-1 ring-emerald-400 dark:text-emerald-500 dark:ring-emerald-700/70"
    : trend === "down"
      ? "bg-white dark:bg-white text-rose-600 ring-1 ring-rose-400 dark:text-rose-300 dark:ring-rose-700/70"
      : "bg-white dark:bg-white text-gray-700 ring-1 ring-gray-300 dark:text-gray-200 dark:ring-gray-700/70";

  const icon = trend === "up" ? "▲" : trend === "down" ? "▼" : "=";

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cls}`} title={`Δ ${label}`}>
      <span aria-hidden>{icon}</span>
      <span>{text}</span>
    </span>
  );
}

function Kpi({ title, value, big, delta, isPercent=false, unit="", decimals }) {
  // Vert plus lisible en light (emerald-600)
  const trendColor =
    !Number.isFinite(delta)
      ? "text-inherit"
      : delta > 0
      ? "text-emerald-600 dark:text-emerald-500"
      : delta < 0
      ? "text-rose-600 dark:text-rose-300"
      : "text-gray-800 dark:text-gray-300";

  return (
    <div className="rounded-xl border p-3 md:p-4 text-center dark:border-gray-700 border-gray-200">
      <div className="text-xs opacity-70">{title}</div>
      <div className={`font-semibold ${big ? "text-2xl mt-1" : "text-lg"} ${trendColor}`}>{value}</div>
      <div className="mt-1 flex justify-center">
        <DeltaChip delta={delta} isPercent={isPercent} unit={unit} decimals={decimals} />
      </div>
    </div>
  );
}
